// engineRpc — the tiny RPC contract between the Studio UI thread and the engine Web Worker.
// The heavy parse/score/reduce work runs off the UI thread (spec §6.3). The dispatcher below is
// PURE (it just calls the engine), so the same function backs both `engine.worker.ts` and the unit
// tests — the round-trip is exercised without spinning up a real Worker.
import type {
  Intent,
  RuleSet,
  Seat,
  TileId,
  WinContext,
  YakuDefMeta,
  GameState,
} from '@mj/protocol';
import {
  parseAgari,
  detectYaku,
  computeFu,
  score,
  scoreTable,
  initialState,
  legalMoves,
  reduce,
  redactFor,
  YAKU_CATALOG,
} from '@mj/engine';
import type { ParsedHand, YakuResult, ReduceResult, RuleError } from '@mj/engine';
import type { ScoreOpts } from '@mj/engine';

// ---- request / response envelopes (discriminated on `method`) -------------
export type EngineRequest =
  | { id: number; method: 'parseAgari'; concealed: TileId[]; melds: import('@mj/protocol').Meld[]; winTile: TileId; winBy: 'ron' | 'tsumo' }
  | { id: number; method: 'detectYaku'; ph: ParsedHand; ctx: WinContext; rs: RuleSet; catalog: YakuDefMeta[]; tiles?: TileId[] }
  | { id: number; method: 'computeFu'; ph: ParsedHand; yr: YakuResult; ctx: WinContext; rs: RuleSet }
  | { id: number; method: 'score'; parses: ParsedHand[]; ctx: WinContext; rs: RuleSet; catalog: YakuDefMeta[]; tiles?: TileId[]; opts?: ScoreOpts }
  | { id: number; method: 'scoreTable'; rs: RuleSet; hans: number[]; fus: number[] }
  | { id: number; method: 'initialState'; rs: RuleSet; seed: number[] }
  | { id: number; method: 'legalMoves'; state: GameState; seat: Seat }
  | { id: number; method: 'reduce'; state: GameState; seat: Seat; intent: Intent; rs: RuleSet; catalog?: YakuDefMeta[] }
  | { id: number; method: 'redactFor'; state: GameState; seat: Seat }
  | { id: number; method: 'catalog' };

export type EngineResponse = { id: number; ok: true; result: unknown } | { id: number; ok: false; error: string };

// Map a method name to the type of its `result` field, for type-safe client calls.
export interface EngineResultMap {
  parseAgari: ParsedHand[];
  detectYaku: YakuResult;
  computeFu: ReturnType<typeof computeFu>;
  score: ReturnType<typeof score>;
  scoreTable: ReturnType<typeof scoreTable>;
  initialState: GameState;
  legalMoves: Intent[];
  reduce: ReduceResult | RuleError;
  redactFor: ReturnType<typeof redactFor>;
  catalog: YakuDefMeta[];
}

/**
 * Pure RPC dispatcher. Given a request, run the matching engine call and return its result.
 * Throws on an unknown method (the caller wraps the throw into an error response).
 */
export function dispatchEngine(req: EngineRequest): unknown {
  switch (req.method) {
    case 'parseAgari':
      return parseAgari(req.concealed, req.melds, req.winTile, req.winBy);
    case 'detectYaku':
      return detectYaku(req.ph, req.ctx, req.rs, req.catalog, req.tiles);
    case 'computeFu':
      return computeFu(req.ph, req.yr, req.ctx, req.rs);
    case 'score':
      return score(req.parses, req.ctx, req.rs, req.catalog, req.tiles, req.opts);
    case 'scoreTable':
      return scoreTable(req.rs, req.hans, req.fus);
    case 'initialState':
      return initialState(req.rs, Uint8Array.from(req.seed));
    case 'legalMoves':
      return legalMoves(req.state, req.seat);
    case 'reduce':
      // Thread the WORKING catalog (when supplied) so custom DSL yaku fire in sandbox win scoring.
      return reduce(req.state, req.seat, req.intent, req.rs, req.catalog ?? YAKU_CATALOG);
    case 'redactFor':
      return redactFor(req.state, req.seat);
    case 'catalog':
      return YAKU_CATALOG;
    default: {
      const _exhaustive: never = req;
      throw new Error(`unknown engine method: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/** Run a request and produce a response envelope (never throws). */
export function runEngineRequest(req: EngineRequest): EngineResponse {
  try {
    return { id: req.id, ok: true, result: dispatchEngine(req) };
  } catch (e) {
    return { id: req.id, ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
